import { useQueryClient } from '@tanstack/react-query';

import type { Guide } from '../../entities/entityTypes';
import { requestBuilder } from '../../http/RequestBuilder';
import type { CreateGuideDto, UpdateGuideDto } from '../types/GuideTypes';

// Create Guide mutation
const useCreateGuide = requestBuilder.mutation<Guide, CreateGuideDto>({
    endpoint: '/api/admin/guides',
    method: 'POST',
});

// Update Guide mutation
const useUpdateGuide = requestBuilder.mutation<
    Guide,
    UpdateGuideDto,
    { id: string }
>({
    endpoint: (urlParams: { id: string }) =>
        `/api/admin/guides/${urlParams.id}`,
    method: 'PATCH',
});

export const useCreateGuideAndRefetch = () => {
    const queryClient = useQueryClient();
    const createGuideMutation = useCreateGuide();

    return {
        createGuideAsync: async (data: CreateGuideDto) => {
            const result = await createGuideMutation.mutateAsync({
                body: data,
            });
            await queryClient.invalidateQueries({ queryKey: ['getAllGuides'] });
            return result;
        },
        isCreating: createGuideMutation.isPending,
        createError: createGuideMutation.error,
    };
};

export const useUpdateGuideAndRefetch = () => {
    const queryClient = useQueryClient();
    const updateGuideMutation = useUpdateGuide();

    return {
        updateGuideAsync: async (id: string, data: UpdateGuideDto) => {
            const result = await updateGuideMutation.mutateAsync({
                body: data,
                urlParams: { id },
            });
            await queryClient.invalidateQueries({ queryKey: ['getAllGuides'] });
            return result;
        },
        isUpdating: updateGuideMutation.isPending,
        updateError: updateGuideMutation.error,
    };
};
